import type { CoverageBand } from '../../objectives/core/coverage';

/**
 * The standards a teacher expected a material to cover, against the ones a check found.
 * Pure — the Planner's hand-off supplies the expected list and a finished check supplies the
 * findings (KTD5).
 *
 * Standards are compared by `caseIdentifierUUID` and nothing else. A statement code is what a
 * teacher reads, but two frameworks can print the same code for different standards, and the
 * id is what the Planner, One Step Ahead and Collab all share.
 */

export interface ExpectedStandard {
  /** The Learning Commons `caseIdentifierUUID`. */
  id: string;
  /** The `statementCode` the teacher reads, e.g. `2-ESS2-3`. */
  code: string;
}

export interface ExpectedComparison {
  /** Expected standards the check found, each with the band it found it at. */
  found: (ExpectedStandard & { band: CoverageBand })[];
  /** Expected standards the check did not find. */
  notFound: ExpectedStandard[];
}

const key = (id: string): string => id.toLowerCase();

/** The list in order with each id once, by its first appearance; ids compare case-insensitively. */
export function dedupeExpected<S extends ExpectedStandard>(list: readonly S[]): S[] {
  const seen = new Set<string>();
  return list.filter((standard) => {
    if (seen.has(key(standard.id))) return false;
    seen.add(key(standard.id));
    return true;
  });
}

/** Both lists keep the expected order; an id expected twice counts once. */
export function compareExpected(
  findings: readonly { caseIdentifierUUID: string; band: CoverageBand }[],
  expected: readonly ExpectedStandard[],
): ExpectedComparison {
  const bands = new Map(findings.map((finding) => [key(finding.caseIdentifierUUID), finding.band]));
  const result: ExpectedComparison = { found: [], notFound: [] };
  for (const standard of dedupeExpected(expected)) {
    const band = bands.get(key(standard.id));
    if (band) result.found.push({ ...standard, band });
    else result.notFound.push(standard);
  }
  return result;
}
